/**
 * Recruiter-facing HTML projection of the reviewed public resume.
 *
 * Content changes must be reviewed together with the published PDF asset and
 * the pinned PDF hash used by the resume-content regression test.
 */
export const resume = {
  name: "Mason D. Cleland",
  headline: "Software Engineer | U.S. Space Force Veteran",
  pdfPath: "/mason_cleland_resume.pdf",
  pdfSha256: "db2a823e7788f2e17d162976786d75c2fcc5f82aad74935f70499b413a10a764",

  profile:
    "Software engineer and U.S. Space Force veteran with experience shipping TypeScript products and maintaining operational SATCOM/RF systems. Builds software, Linux Infrastructure, and hardware-integrated workflows using bounded agentic development, automated validation, and human-controlled change promotion.",

  contact: [
    {
      label: "Phone",
      value: "(951) 206-7048",
      href: "tel:+19512067048",
    },
    {
      label: "Email",
      value: "Mason.D.Cleland@gmail.com",
      href: "mailto:Mason.D.Cleland@gmail.com",
    },
    {
      label: "Website",
      value: "clelandmason.com",
      href: "https://clelandmason.com",
    },
    {
      label: "GitHub",
      value: "github.com/Gizmo8095",
      href: "https://github.com/Gizmo8095",
    },
  ],

  technicalExpertise: [
    {
      title: "Application engineering",
      detail:
        "TypeScript, JavaScript, Python, Rust, Lua, SQL, Bash; React, Next.js, Node.js, Express, Tauri, GraphQL",
    },
    {
      title: "Systems and DevSecOps",
      detail:
        "Linux, Docker, Podman, GitHub Actions, CI/CD, system configuration, state/drift validation, rollback, backup and recovery",
    },
    {
      title: "Data, AI and geospatial",
      detail:
        "PostgreSQL, PostGIS, HNSW, transformer embeddings, Ollama, Turf.js",
    },
  ],

  systemsIntegration: [
    {
      title: "Physical systems",
      detail: "SATCOM/RF, Networking, Field Diagnostics",
    },
    {
      title: "Software Integration",
      detail: "Sensor Ingestion, Geospatial Tooling, System Interfaces",
    },
    {
      title: "Operational Engineering",
      detail: "Containerization, Rollback & Recovery, Controlled Change",
    },
  ],

  references: [
    {
      name: "Rob Murtha",
      phone: "(254) 449-0185",
      phoneHref: "tel:+12544490185",
      email: "rob@adj.ai",
      emailHref: "mailto:rob@adj.ai",
    },
    {
      name: "TSgt David Rosenberg",
      phone: "(860) 328-1371",
      phoneHref: "tel:+18603281371",
      email: "david.rosenberg.3@spaceforce.mil",
      emailHref: "mailto:david.rosenberg.3@spaceforce.mil",
    },
    {
      name: "Zachary Smith",
      phone: "(256) 453-9384",
      phoneHref: "tel:+12564539384",
      email: "zachary.clay.smith@gmail.com",
      emailHref: "mailto:zachary.clay.smith@gmail.com",
    },
  ],

  experience: [
    {
      role: "Software Engineer",
      organization: "Adjective",
      dates: "August 2024 - March 2025",
      bullets: [
        "Built and published PulseflowJS, a TypeScript scientific SDK with zero production dependencies, strict typing, documented public APIs, comprehensive Jest testing, and automated test/build gates; reached 1,000+ npm downloads in six months.",
        "Productized Barnes wind-field interpolation originally developed for NuMARSS into reusable meteorological and geospatial tooling for generating interpolated wind grids from sparse observations.",
        "Implemented reusable statistical and geospatial modules for interpolation, filtering, classification, and distance/bearing analysis.",
        "Led R&D and prototyped an end-to-end semantic-profile pipeline combining document extraction, schema-driven transformation, transformer embeddings, HNSW retrieval, and local LLM inference to evaluate product feasibility.",
      ],
    },
    {
      role: "Junior Developer",
      organization: "Metronome",
      dates: "August 2023 - November 2023",
      bullets: [
        "Implemented Barnes objective analysis to interpolate sparse weather-tower observations into continuous wind fields used for rocket-launch feasibility assessments and airborne chemical-hazard movement analysis.",
        "Built validated, retry-safe sensor-data ingestion and transformation pipelines plus geospatial modules for sensor mapping, wind visualization, risk visualization, and clustering.",
        "Tuned PostGIS spatial queries and expanded Jest coverage for critical ingestion and geospatial workflows.",
      ],
    },
    {
      role: "SATCOM Systems Crew Chief",
      organization: "Department of the Air Force",
      dates: "September 2019 - August 2024",
      bullets: [
        "Maintained and troubleshot fixed SATCOM terminals and associated RF, networking, computing, and support equipment for operational command-and-control (C2) communications.",
        "Performed system imaging, network and host configuration, backup/recovery, and field administration during maintenance and rapid restoration.",
        "Led cross-layer diagnostics from physical/RF faults through network and host issues; directed an eight-member technical detachment and secure data handling practices.",
      ],
    },
  ],

  projects: [
    {
      name: "Bazzite Workstation Configuration",
      technologies: "Python, Bash, Linux, Git",
      bullets: [
        "Built a Git-backed configuration and validation system for an immutable Bazzite DX workstation, including state capture, drift auditing, controlled update/reconciliation, compatibility checks, rollback evidence, destructive-operation safeguards, and a 44-test validation suite.",
        "Defined agent permissions, automated validation, and human promotion gates so generated changes can be reviewed without granting write access to the authoritative repository.",
      ],
    },
    {
      name: "Pokemon Battle Analysis Platform",
      technologies: "Rust, Typescript, React, Tauri",
      bullets: [
        "Architected a multi-layer application spanning Rust battle/domain logic, application services, persistence, generated TypeScript contracts, React clients, and HTTP/Tauri adapters.",
        "Validated battle-engine, domain-model, and data behavior through 24 automated tests, generated contract checks, and clean Rust linting.",
      ],
    },
  ],
} as const;
